/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollPanel } from 'primereact/scrollpanel';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';

import { Alarm } from '../../../../../../../src/app/typescript-axios';
import { MarsinetContext } from '../../../../../context/marsinetProvider';

import './event-list.module.scss';
import Graphs from '../../../maintenance/components/graphs/graphs';
import { EventListProps } from '../../../../../../../src/app/models/alarmsModels';
import { apiServices } from '../../../../../../../src/app/services/apiServices';
import { isAtlasModel } from '@marsinet/commons';
import { getAlarmTranslationKey } from 'apps/marsinet/src/app/utils/utils';
import { formatUtcTimestamp } from 'apps/marsinet/src/app/utils/timezone-utils';

export function EventList(props: Readonly<EventListProps>) {
  const { t } = useTranslation();
  const {
    alarms,
    setUpdateAlarms,
    loading,
    incidence,
    model,
    setSelectedAlarms,
  } = props;
  const { selectedTimezone } = useContext(MarsinetContext);
  const locale = navigator.language || 'en-US';
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [prevIsModalVisible, setPrevIsModalVisible] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState<any | null>(null);
  const [showGraphs, setShowGraphs] = useState<boolean>(false);
  const [ingestionId, setIngestionId] = useState<string>('');
  const [zip_data, setZip_data] = useState<string>('');
  const [serialNumber, setSerialNumber] = useState<string>('');
  // Detectar si el dispositivo es Atlas.
  const isAtlas = isAtlasModel(Number(model));

  useEffect(() => {
    setPrevIsModalVisible(isModalVisible);
  }, [isModalVisible, prevIsModalVisible, setUpdateAlarms]);

  const obtenerFechaYHoraDeTimestamp = (timestamp: string) => {
    // Las fechas de eventos llegan en UTC y se formatean según la zona e idioma elegidos.
    return formatUtcTimestamp(timestamp, selectedTimezone, locale, {
      weekday: incidence ? 'long' : undefined,
      month: incidence ? 'long' : undefined,
      day: incidence ? '2-digit' : undefined,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const getPaddingClass = (item: any) => {
    if (item.status === 'active') {
      if (item.event_type === 'Alarm') {
        return 'pl-5';
      } else if (item.event_type === 'Warning') {
        return 'pl-2';
      } else {
        return '';
      }
    } else {
      return 'pl-0';
    }
  };

  const itemsTemplate = (item: Alarm, index: number) => {
    const fechaHora = obtenerFechaYHoraDeTimestamp(item.timestamp);
    const circleColor = item.value === 1 ? '#13dfb6' : 'gray';
    const statusColor = item.value === 1 ? '#13dfb6' : 'gray';
    const eyeIconColor = '#13dfb6';
    const paddingClass = getPaddingClass(item);

    return (
      <div
        className="flex items-center border p-3 mb-1 bg-white rounded shadow-md"
        key={index}
      >
        <div className="flex items-center text-lg mr-4">
          <span
            data-tip={item.value === 1 ? 'Activo' : 'Inactivo'}
            data-for={`tooltip-${index}`}
          >
            <i
              className={`pi ${item.value === 1 ? 'pi-circle-fill' : 'pi-circle'} text-lg`}
              style={{ color: circleColor }}
            />
          </span>
          <span
            className={`ml-2 text-lg ${item.value === 1 ? 'font-bold' : ''}`}
            style={{ color: statusColor }}
          >
            {item.value === 1
              ? t('pages.alarms.active')
              : t('pages.alarms.inactive')}
          </span>
        </div>
        <div className={`text-lg ml-4 ${paddingClass}`}>
          <span>{fechaHora}</span>
        </div>
        <div className="text-lg ml-4">
          <span className="font-semibold">{item.event_type}</span> -{' '}
          {item.event_id}
        </div>
        <div
          className={`text-lg ml-4 ${paddingClass}`}
          style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}
        >
          {t(getAlarmTranslationKey(item.event_id, isAtlas))}
        </div>
        <div className="ml-auto">
          <Button
            className="p-button-rounded p-button-secondary p-button-text p-button-lg"
            onClick={() => showEventModal(item)}
            aria-label="Ver"
            tabIndex={0}
            style={{
              cursor: 'pointer',
              color: eyeIconColor,
              border: 'none',
              background: 'none',
            }}
            data-tip="Ver detalles"
            data-for={`tooltip-${index}`}
          >
            <i className="pi pi-eye" />
          </Button>
        </div>
      </div>
    );
  };

  //filtrado de alarmas seleccionadas por fecha previamente
  const filteredAlarms = alarms.filter(
    (event) =>
      event.event_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.event_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.version.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const showEventModal = (event: Alarm) => {
    setSelectedAlarm(event);
    if (setSelectedAlarms) {
      setSelectedAlarms((prevAlarms) => {
        const exists = prevAlarms.some(
          (a) =>
            a.event_id === event.event_id && a.timestamp === event.timestamp
        );
        return exists ? prevAlarms : [...prevAlarms, event];
      });
    }
    setIsModalVisible(true);
  };

  const hideEventModal = () => {
    setSelectedAlarm(null);
    setIsModalVisible(false);
  };

  const handleViewDetails = async (event) => {
    event.preventDefault();
    setShowGraphs(true);
    setIngestionId(selectedAlarm.i_id);

    const ingestionData: any =
      await apiServices.ingestionApi.ingestionControllerGetCompressedFileName(
        selectedAlarm.i_id
      );
    setZip_data(ingestionData.data.zip_data);

    const deviceData: any =
      await apiServices.devicesApi.devicesControllerFindOne(
        ingestionData.data.d_id
      );
    setSerialNumber(deviceData.data.serial);
  };

  const modalContent = (
    <div>
      {selectedAlarm && (
        <div className="p-4">
          <p className="mb-2">
            <span className="font-semibold">{t('pages.alarms.date')}:</span>{' '}
            {obtenerFechaYHoraDeTimestamp(selectedAlarm.timestamp)}
          </p>
          <p className="mb-2">
            <span className="font-semibold">
              {t('pages.alarms.eventType')}:
            </span>{' '}
            {selectedAlarm.event_type}
          </p>
          <p className="mb-2">
            <span className="font-semibold">{t('pages.alarms.eventId')}:</span>{' '}
            {selectedAlarm.event_id}
          </p>
          <p className="mb-2">
            <span className="font-semibold">{t('pages.alarms.message')}:</span>{' '}
            {t(getAlarmTranslationKey(selectedAlarm.event_id, isAtlas))}
          </p>
          <p className="mb-2">
            <span className="font-semibold">{t('pages.alarms.value')}:</span>{' '}
            {selectedAlarm.value}
          </p>
          <p className="mb-2">
            <span className="font-semibold">{t('pages.alarms.version')}:</span>{' '}
            {selectedAlarm.version}
          </p>
          <p className="mb-2">
            <span className="font-semibold">{t('pages.alarms.ingest')}</span>
            {' →  '}
            <a
              href={`/alarms/${selectedAlarm.i_id}`}
              className="underline text-blue-600 hover:text-blue-800"
              onClick={async (e) => {
                handleViewDetails(e);
              }}
            >
              {t('pages.ingestions.graphs')}
            </a>
          </p>
        </div>
      )}
    </div>
  );

  return showGraphs ? (
    <Graphs
      ingestionId={ingestionId}
      setShowGraphs={setShowGraphs}
      zip_data={zip_data}
      isTest={false}
      serialNumber={serialNumber}
    />
  ) : (
    <div className="mt-3 py-4 border-2 surface-border border-round-2xl border-dashed surface-card">
      <div className="mb-3">
        <div className="flex justify-start px-2">
          <span className="p-input-icon-left w-full">
            <i className="pi pi-search" />
            <InputText
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('pages.alarms.search')}
              className="w-full border-1"
            />
          </span>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-content-center align-items-center h-full">
          <ProgressSpinner />
        </div>
      ) : (
        <ScrollPanel
          style={{
            width: '100%',
            height: filteredAlarms.length > 2 ? '319px' : '200px',
            overflow: 'visible',
          }}
          className="custombar1 px-1"
        >
          {filteredAlarms.length > 0 ? (
            filteredAlarms.map((event, i) => itemsTemplate(event, i))
          ) : (
            <div className="text-center ">{t('pages.alarms.noAlarms')}</div>
          )}
        </ScrollPanel>
      )}
      <Dialog
        visible={isModalVisible}
        onHide={hideEventModal}
        header={t('pages.alarms.details')}
      >
        {modalContent}
      </Dialog>
    </div>
  );
}

export default EventList;
